/**
 * AdminPage (`/admin`)
 * ------------------------------------------------------------------
 * Bayenah operations console. Dark monochrome to match the landing
 * page. Three tabs: Pipeline · Metrics · Operations.
 *
 * Auth: password-gated via POST /api/admin/login; the same password
 * is then passed as ?password= to every read/write endpoint. Session
 * lives in sessionStorage with a 30 min idle timeout.
 *
 * Data sources (unchanged from previous iteration — only the UI was
 * redesigned):
 *   - GET  /api/admin/login              auth
 *   - GET  /api/admin/stats              questions, users, weak responses
 *   - GET  /api/admin/investor-metrics   KPIs, signup trend, roles
 *   - GET  /api/admin/waitlist           pipeline rows + counts
 *   - POST /api/admin/waitlist/{id}/status        advance status
 *   - POST /api/admin/waitlist/{id}/grant-access  approve + magic link
 *   - POST /api/admin/waitlist/{id}/reject        reject + reason
 *   - POST /api/admin/waitlist/{id}/note          append admin note
 *   - GET  /api/_debug/version           build fingerprint (?debug=1 only)
 *
 * Developer footer is hidden by default — append `?debug=1` to the
 * URL to surface the version chip.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const BACKEND_URL = getBackendBaseUrl();
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

// ── design tokens ──────────────────────────────────────────────────
const C = {
  bg:       '#000000',
  surface:  '#0D0D0D',
  card:     '#111111',
  hover:    '#0D0D0D',
  expanded: '#080808',
  border:   '#1A1A1A',
  divider:  '#222222',
  text:     '#FFFFFF',
  muted:    'rgba(255,255,255,0.4)',
  subtle:   'rgba(255,255,255,0.2)',
  ghost:    'rgba(255,255,255,0.08)',
};
const FONT = "'Helvetica Neue', Arial, sans-serif";

// ── helpers ────────────────────────────────────────────────────────
const fmt = (n) => {
  if (n === null || n === undefined) return null;
  if (typeof n === 'string') return n;
  if (typeof n === 'number' && !Number.isFinite(n)) return null;
  return new Intl.NumberFormat('en-US').format(n);
};

function relativeDate(iso) {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const diff = Date.now() - then;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(then).toISOString().slice(0, 10);
}

function roleLabel(slug) {
  if (!slug) return '—';
  return slug
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// ── main page ──────────────────────────────────────────────────────
export default function AdminPage() {
  const [password, setPassword] = useState('');
  const [operatorName, setOperatorName] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('Pipeline');
  const [stats, setStats] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [version, setVersion] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const debugMode = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('debug') === '1';
  }, []);

  // noindex meta
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  // build fingerprint (debug only)
  useEffect(() => {
    if (!debugMode) return;
    fetch(`${BACKEND_URL}/api/_debug/version`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setVersion)
      .catch(() => {});
  }, [debugMode]);

  // restore session
  useEffect(() => {
    const stored = sessionStorage.getItem('admin_password');
    const storedOp = sessionStorage.getItem('admin_operator_name');
    if (stored && storedOp) {
      setPassword(stored);
      setOperatorName(storedOp);
      setIsAuthenticated(true);
      fetchStats(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // idle timeout
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const bump = () => sessionStorage.setItem('admin_last_active', Date.now().toString());
    bump();
    const tick = setInterval(() => {
      const last = Number(sessionStorage.getItem('admin_last_active') || 0);
      if (last && Date.now() - last > SESSION_TIMEOUT_MS) handleLogout();
    }, 60000);
    window.addEventListener('mousemove', bump);
    window.addEventListener('keydown', bump);
    window.addEventListener('click', bump);
    return () => {
      clearInterval(tick);
      window.removeEventListener('mousemove', bump);
      window.removeEventListener('keydown', bump);
      window.removeEventListener('click', bump);
    };
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setError(''); setLoading(true);
    const opName = operatorName.trim();
    if (!opName) {
      setError('Operator name is required.');
      setLoading(false);
      return;
    }
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      if (r.ok) {
        sessionStorage.setItem('admin_password', password);
        sessionStorage.setItem('admin_operator_name', opName);
        setOperatorName(opName);
        setIsAuthenticated(true);
        fetchStats(password);
      } else {
        setError('Incorrect password.');
      }
    } catch (_e) {
      setError('Failed to connect.');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async (pwd) => {
    setRefreshing(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/stats?password=${encodeURIComponent(pwd)}`);
      if (r.ok) {
        setStats(await r.json());
        setLastUpdated(new Date());
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleRefresh = () => fetchStats(password);

  const handleLogout = () => {
    sessionStorage.removeItem('admin_password');
    sessionStorage.removeItem('admin_operator_name');
    sessionStorage.removeItem('admin_last_active');
    setIsAuthenticated(false);
    setPassword('');
    setOperatorName('');
    setStats(null);
    setError('');
  };

  if (!isAuthenticated) {
    return (
      <LoginView
        password={password}
        setPassword={setPassword}
        operatorName={operatorName}
        setOperatorName={setOperatorName}
        onLogin={handleLogin}
        loading={loading}
        error={error}
      />
    );
  }

  return (
    <div
      style={{ minHeight: '100vh', background: C.bg, color: C.text, fontFamily: FONT }}
      data-testid="admin-page"
    >
      <HeaderBar
        lastUpdated={lastUpdated}
        refreshing={refreshing}
        onRefresh={handleRefresh}
        onLogout={handleLogout}
        operatorName={operatorName}
      />
      <TabBar activeTab={activeTab} setActiveTab={setActiveTab} />
      <main style={{ padding: '32px 40px 80px' }}>
        {activeTab === 'Pipeline' && (
          <PipelineTab password={password} backendUrl={BACKEND_URL} operatorName={operatorName} />
        )}
        {activeTab === 'Metrics' && (
          <MetricsTab password={password} backendUrl={BACKEND_URL} stats={stats} />
        )}
        {activeTab === 'Operations' && (
          <OperationsTab stats={stats} />
        )}
      </main>
      {debugMode && version && <DebugStrip version={version} />}
    </div>
  );
}

// ── login view ─────────────────────────────────────────────────────
function LoginView({ password, setPassword, operatorName, setOperatorName, onLogin, loading, error }) {
  const canSubmit = !loading && password && operatorName && operatorName.trim().length >= 2;
  return (
    <div
      data-testid="admin-login-view"
      style={{
        minHeight: '100vh',
        background: C.bg,
        color: C.text,
        fontFamily: FONT,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 32,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <img src="/brand/mark-white.png" alt="" aria-hidden="true" style={{ height: 24, opacity: 0.6 }} />
        <span style={{ fontSize: 14, fontWeight: 600, color: 'rgba(255,255,255,0.6)' }}>
          Admin
        </span>
      </div>
      <form
        onSubmit={onLogin}
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 4,
          padding: 32,
          width: 360,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 4 }}>
          Sign in
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.3)', marginBottom: 24 }}>
          Bayenah admin access · operator name required for audit
        </div>
        <label
          htmlFor="admin-operator-name-input"
          style={{ display: 'block', fontSize: 10, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase' }}
        >
          Operator name <span style={{ color: '#c8a565' }}>*</span>
        </label>
        <input
          id="admin-operator-name-input"
          type="text"
          placeholder="Your name (for the audit log)"
          value={operatorName}
          onChange={(e) => setOperatorName(e.target.value)}
          data-testid="admin-operator-name-input"
          autoFocus
          maxLength={80}
          autoComplete="name"
          style={{
            width: '100%',
            background: C.bg,
            border: `1px solid ${C.divider}`,
            borderRadius: 3,
            padding: '10px 14px',
            color: C.text,
            fontSize: 13,
            marginBottom: 14,
            outline: 'none',
            fontFamily: FONT,
            boxSizing: 'border-box',
          }}
        />
        <label
          htmlFor="admin-password-input"
          style={{ display: 'block', fontSize: 10, letterSpacing: '0.14em', color: 'rgba(255,255,255,0.4)', marginBottom: 6, textTransform: 'uppercase' }}
        >
          Password <span style={{ color: '#c8a565' }}>*</span>
        </label>
        <input
          id="admin-password-input"
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid="admin-password-input"
          autoComplete="current-password"
          style={{
            width: '100%',
            background: C.bg,
            border: `1px solid ${C.divider}`,
            borderRadius: 3,
            padding: '10px 14px',
            color: C.text,
            fontSize: 13,
            marginBottom: 12,
            outline: 'none',
            fontFamily: FONT,
            boxSizing: 'border-box',
          }}
        />
        {error && (
          <div data-testid="admin-login-error" style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 12 }}>
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={!canSubmit}
          data-testid="admin-login-btn"
          style={{
            width: '100%',
            background: '#FFFFFF',
            color: '#000',
            border: 'none',
            borderRadius: 3,
            padding: 10,
            fontSize: 13,
            fontWeight: 600,
            cursor: !canSubmit ? 'not-allowed' : 'pointer',
            opacity: !canSubmit ? 0.5 : 1,
            fontFamily: FONT,
          }}
        >
          {loading ? 'Signing in…' : 'Sign in'}
        </button>
        <p style={{ marginTop: 14, fontSize: 11, color: 'rgba(255,255,255,0.25)', lineHeight: 1.55 }}>
          Your operator name is stamped on every action you take in this session — every approve, decline, and note lands in <code style={{ color: 'rgba(255,255,255,0.4)' }}>admin_audit_log</code> under your name.
        </p>
      </form>
    </div>
  );
}

// ── header ─────────────────────────────────────────────────────────
function HeaderBar({ lastUpdated, refreshing, onRefresh, onLogout, operatorName }) {
  const stamp = lastUpdated ? relativeDate(lastUpdated.toISOString()) : '—';
  return (
    <header
      data-testid="admin-header"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 40px',
        borderBottom: `1px solid ${C.border}`,
        background: C.bg,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <img src="/brand/mark-white.png" alt="" aria-hidden="true" style={{ height: 20, opacity: 0.8 }} />
        <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>Bayenah</span>
        <span style={{ width: 1, height: 14, background: C.divider }} />
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', letterSpacing: '0.1em' }}>
          ADMIN · PRODUCTION
        </span>
        {operatorName ? (
          <>
            <span style={{ width: 1, height: 14, background: C.divider }} />
            <span data-testid="admin-operator-stamp" style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.04em' }}>
              Operator: <b style={{ color: '#fff', fontWeight: 600 }}>{operatorName}</b>
            </span>
          </>
        ) : null}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        <span style={{ fontSize: 11, color: C.subtle }}>
          Last updated: {stamp}
        </span>
        <SecondaryButton onClick={onRefresh} disabled={refreshing} testid="admin-refresh-btn">
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </SecondaryButton>
        <SecondaryButton onClick={onLogout} testid="admin-logout-btn">
          Sign out
        </SecondaryButton>
      </div>
    </header>
  );
}

function SecondaryButton({ children, onClick, disabled, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      style={{
        background: 'none',
        border: `1px solid ${C.divider}`,
        color: C.muted,
        padding: '6px 14px',
        borderRadius: 3,
        fontSize: 11,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: FONT,
        opacity: disabled ? 0.5 : 1,
        letterSpacing: '0.02em',
      }}
    >
      {children}
    </button>
  );
}

// ── tab bar ────────────────────────────────────────────────────────
function TabBar({ activeTab, setActiveTab }) {
  const tabs = ['Pipeline', 'Metrics', 'Operations'];
  return (
    <nav
      data-testid="admin-tab-bar"
      style={{
        display: 'flex',
        gap: 0,
        borderBottom: `1px solid ${C.border}`,
        padding: '0 40px',
        background: C.bg,
      }}
    >
      {tabs.map((tab) => {
        const active = activeTab === tab;
        return (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            data-testid={`admin-tab-${tab.toLowerCase()}`}
            style={{
              padding: '14px 24px',
              background: 'none',
              border: 'none',
              borderBottom: `2px solid ${active ? '#fff' : 'transparent'}`,
              color: active ? '#fff' : 'rgba(255,255,255,0.3)',
              fontSize: 13,
              fontWeight: active ? 500 : 400,
              cursor: 'pointer',
              letterSpacing: '0.04em',
              fontFamily: FONT,
            }}
          >
            {tab}
          </button>
        );
      })}
    </nav>
  );
}

// ── Pipeline tab ───────────────────────────────────────────────────
const STATUS_PILLS = [
  { key: '',          label: 'All' },
  { key: 'pending',   label: 'Pending' },
  { key: 'reviewed',  label: 'Reviewed' },
  { key: 'approved',  label: 'Approved' },
  { key: 'rejected',  label: 'Rejected' },
];
const NEXT_STATUS = { pending: 'reviewed', reviewed: 'approved' };

function statusPillStyle(status) {
  const map = {
    pending:   { border: '#333',                       color: 'rgba(255,255,255,0.4)', label: 'Pending'  },
    reviewed:  { border: '#444',                       color: 'rgba(255,255,255,0.6)', label: 'Reviewed' },
    approved:  { border: 'rgba(255,255,255,0.4)',      color: '#fff',                  label: 'Approved' },
    activated: { border: '#fff',                       color: '#fff',                  label: 'Activated' },
    rejected:  { border: '#333',                       color: 'rgba(255,255,255,0.2)', label: 'Rejected' },
  };
  return map[status] || { border: '#333', color: 'rgba(255,255,255,0.4)', label: status || '—' };
}

function PipelineTab({ password, backendUrl, operatorName }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [statusFilter, setStatusFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [busyRow, setBusyRow] = useState(null);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Mobile detection: switches the 7-column data table for a card-per-
  // row layout at ≤768px so action buttons and the "Rejected" column
  // are reachable without horizontal scroll. May-24 audit, item #3.
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth <= 768,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const load = useCallback(async () => {
    setError('');
    try {
      const params = new URLSearchParams({ password });
      if (statusFilter) params.append('status', statusFilter);
      const r = await fetch(`${backendUrl}/api/admin/waitlist?${params}`);
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || 'Failed to load');
      setRows(data.items || []);
      setCounts(data.by_status || {});
    } catch (e) {
      setError(e.message);
    }
  }, [password, backendUrl, statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const post = async (path, body) => {
    // operator_name stamps every admin action into admin_audit_log.
    const qs = new URLSearchParams({ password, operator_name: operatorName || '' });
    const r = await fetch(`${backendUrl}/api/admin/waitlist/${path}?${qs}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data?.detail || 'Request failed');
    return data;
  };

  const onApprove = async (row) => {
    const next = NEXT_STATUS[row.status];
    setBusyRow(row.request_id);
    try {
      if (next) {
        await post(`${row.request_id}/status`, { status: next });
        setToast(`Moved to ${next}`);
      } else if (row.status === 'approved') {
        if (!window.confirm(`Grant Bayenah access to ${row.work_email}?\nA welcome magic-link email will be sent.`)) {
          setBusyRow(null);
          return;
        }
        const data = await post(`${row.request_id}/grant-access`, { include_colleagues: true });
        const errs = (data.errors || []).length;
        setToast(`✓ Access granted${errs ? ` (${errs} error${errs > 1 ? 's' : ''} — check console)` : ''}`);
        if (errs) console.error('grant-access errors:', data.errors);
      }
      await load();
    } catch (e) { setError(e.message); } finally { setBusyRow(null); }
  };

  const onReject = async (row) => {
    const reason = window.prompt('Decline reason (optional, admin-only — logged to admin_audit_log):', '');
    if (reason === null) return;
    setBusyRow(row.request_id);
    try {
      await post(`${row.request_id}/reject`, { reason });
      setToast('Declined');
      await load();
    } catch (e) { setError(e.message); } finally { setBusyRow(null); }
  };

  const onNote = async (row) => {
    const text = window.prompt('Internal note (admin-only):', '');
    if (!text || !text.trim()) return;
    setBusyRow(row.request_id);
    try {
      await post(`${row.request_id}/note`, { text: text.trim(), author: 'admin' });
      setToast('Note saved');
      await load();
    } catch (e) { setError(e.message); } finally { setBusyRow(null); }
  };

  const exportCsv = () => {
    if (!rows.length) return;
    const headers = ['Name', 'Email', 'Company', 'Role', 'Team Size', 'Monthly Volume', 'Status', 'Source', 'Submitted'];
    const data = rows.map((r) => [
      r.full_name, r.work_email, r.company_name, roleLabel(r.role),
      r.team_size, r.monthly_volume, r.status, r.source, r.created_at,
    ]);
    const csv = [headers, ...data]
      .map((row) => row.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `bayenah-pipeline-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const thisWeek = (counts.pending || 0) + (counts.reviewed || 0); // best-effort proxy

  return (
    <section data-testid="admin-pipeline-tab">
      {/* Mobile-aware row 1 (filter pills + actions). Wraps a single
          horizontal-scroll container so the "Rejected" chip, "Export
          CSV" and "+ Invite someone" are reachable at 375px without
          getting clipped off-screen. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 18,
          gap: 12,
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingBottom: 4,
        }}
        // Inline ::-webkit-scrollbar suppression — keep the horizontal
        // scroll silent so the row reads as a normal chip strip.
        className="admin-no-scrollbar"
      >
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          {STATUS_PILLS.map((p) => {
            const active = statusFilter === p.key;
            return (
              <button
                key={p.key || 'all'}
                type="button"
                onClick={() => setStatusFilter(p.key)}
                data-testid={`pipeline-filter-${p.key || 'all'}`}
                style={{
                  background: active ? '#fff' : 'none',
                  border: `1px solid ${active ? '#fff' : C.divider}`,
                  color: active ? '#000' : C.muted,
                  padding: '6px 12px',
                  borderRadius: 3,
                  fontSize: 11,
                  cursor: 'pointer',
                  fontFamily: FONT,
                  letterSpacing: '0.04em',
                  fontWeight: active ? 600 : 400,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
          <SecondaryButton onClick={exportCsv} testid="pipeline-export-csv">
            Export CSV
          </SecondaryButton>
          <button
            type="button"
            onClick={() => setShowInviteModal(true)}
            data-testid="pipeline-invite-btn"
            style={{
              background: '#fff',
              color: '#000',
              border: 'none',
              padding: '8px 18px',
              borderRadius: 3,
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              letterSpacing: '0.04em',
              fontFamily: FONT,
              whiteSpace: 'nowrap',
            }}
          >
            + Invite someone
          </button>
        </div>
      </div>

      {/* Summary strip */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 24,
      }}>
        {[
          { label: 'Pending review', value: counts.pending },
          { label: 'Approved',       value: (counts.approved || 0) + (counts.activated || 0) },
          { label: 'Rejected',       value: counts.rejected },
          { label: 'This week',      value: thisWeek },
        ].map((chip) => (
          <div
            key={chip.label}
            data-testid={`pipeline-chip-${chip.label.toLowerCase().replace(/\s+/g, '-')}`}
            style={{
              background: C.surface,
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              padding: '14px 18px',
            }}
          >
            <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginBottom: 4 }}>
              {chip.label}
            </div>
            <div style={{ fontSize: 22, fontWeight: 600, color: chip.value ? '#fff' : 'rgba(255,255,255,0.25)' }}>
              {fmt(chip.value ?? 0)}
            </div>
          </div>
        ))}
      </div>

      {error && <div style={{ marginBottom: 12, color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>{error}</div>}

      {/* Table */}
      {rows.length === 0 ? (
        <EmptyState
          title="No access requests yet."
          subtitle="Share bayenah.com to start receiving applications."
        />
      ) : isMobile ? (
        // ── Mobile card layout (≤768px) ─────────────────────────
        // Each applicant becomes a self-contained card with full
        // action buttons. Avoids the unreadable 7-column grid + the
        // clipped "Reject" / "Approve" buttons.
        <div
          data-testid="pipeline-cards"
          style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        >
          {rows.map((row) => {
            const s = statusPillStyle(row.status);
            const submitted = new Date(row.created_at).toLocaleDateString();
            return (
              <div
                key={row.request_id}
                data-testid={`pipeline-card-${row.request_id}`}
                style={{
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 4,
                  padding: 16,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
                    {row.full_name || row.work_email}
                  </div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
                    {row.work_email}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)' }}>
                  {row.company_name || '—'} · {roleLabel(row.role)}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>
                  Team: {row.team_size || '—'} · Volume: {row.monthly_volume || '—'}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span
                    style={{
                      fontSize: 10,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: s.color,
                      border: `1px solid ${s.border}`,
                      padding: '2px 8px',
                      borderRadius: 3,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {s.label}
                  </span>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>{submitted}</span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    paddingTop: 4,
                    borderTop: `1px solid ${C.border}`,
                    marginTop: 4,
                  }}
                >
                  <button
                    type="button"
                    disabled={busyRow === row.request_id}
                    onClick={() => onApprove(row)}
                    data-testid={`pipeline-card-approve-${row.request_id}`}
                    style={{
                      flex: 1,
                      background: '#fff',
                      color: '#000',
                      border: 'none',
                      padding: '8px 0',
                      borderRadius: 3,
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: FONT,
                      cursor: busyRow === row.request_id ? 'wait' : 'pointer',
                      opacity: busyRow === row.request_id ? 0.6 : 1,
                    }}
                  >
                    {row.status === 'approved' ? 'Grant access' : NEXT_STATUS[row.status] ? `→ ${NEXT_STATUS[row.status]}` : 'Approve'}
                  </button>
                  <button
                    type="button"
                    disabled={busyRow === row.request_id}
                    onClick={() => onReject(row)}
                    data-testid={`pipeline-card-reject-${row.request_id}`}
                    style={{
                      flex: 1,
                      background: 'transparent',
                      color: '#ff6b6b',
                      border: `1px solid ${C.border}`,
                      padding: '8px 0',
                      borderRadius: 3,
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: FONT,
                      cursor: busyRow === row.request_id ? 'wait' : 'pointer',
                    }}
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    onClick={() => onNote(row)}
                    data-testid={`pipeline-card-note-${row.request_id}`}
                    style={{
                      background: 'transparent',
                      color: 'rgba(255,255,255,0.6)',
                      border: `1px solid ${C.border}`,
                      padding: '8px 12px',
                      borderRadius: 3,
                      fontSize: 12,
                      fontFamily: FONT,
                      cursor: 'pointer',
                    }}
                  >
                    Note
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div
          data-testid="pipeline-table"
          style={{
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          {/* header */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1.5fr 1.5fr 1.2fr 0.8fr 0.9fr 0.9fr 1.4fr',
            padding: '12px 20px',
            borderBottom: `1px solid ${C.border}`,
            fontSize: 10,
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'rgba(255,255,255,0.3)',
          }}>
            <div>Name</div><div>Company</div><div>Role</div><div>Team Size</div>
            <div>Submitted</div><div>Status</div><div style={{ textAlign: 'right' }}>Actions</div>
          </div>
          {rows.map((row) => (
            <PipelineRow
              key={row.request_id}
              row={row}
              expanded={expanded === row.request_id}
              busy={busyRow === row.request_id}
              onExpand={() => setExpanded(expanded === row.request_id ? null : row.request_id)}
              onApprove={() => onApprove(row)}
              onReject={() => onReject(row)}
              onNote={() => onNote(row)}
            />
          ))}
        </div>
      )}

      {toast && (
        <div
          data-testid="pipeline-toast"
          style={{
            position: 'fixed', bottom: 24, right: 24,
            background: '#fff', color: '#000',
            padding: '10px 16px', borderRadius: 3,
            fontSize: 12, fontWeight: 500, fontFamily: FONT,
            boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
          }}
        >
          {toast}
        </div>
      )}

      {showInviteModal && (
        <InviteModal
          password={password}
          backendUrl={backendUrl}
          onClose={() => setShowInviteModal(false)}
          onSuccess={(email) => { setToast(`✓ Invitation sent to ${email}`); load(); }}
        />
      )}
    </section>
  );
}

function PipelineRow({ row, expanded, busy, onExpand, onApprove, onReject, onNote }) {
  const [hover, setHover] = useState(false);
  const pill = statusPillStyle(row.status);
  const canApprove = ['pending', 'reviewed', 'approved'].includes(row.status);
  const approveLabel = row.status === 'approved' ? 'Grant Access' : 'Approve';

  return (
    <>
      <div
        onClick={onExpand}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        data-testid={`pipeline-row-${row.request_id}`}
        style={{
          display: 'grid',
          gridTemplateColumns: '1.5fr 1.5fr 1.2fr 0.8fr 0.9fr 0.9fr 1.4fr',
          padding: '14px 20px',
          alignItems: 'center',
          background: expanded ? C.expanded : hover ? C.hover : 'transparent',
          borderTop: `1px solid ${C.border}`,
          cursor: 'pointer',
          fontFamily: FONT,
        }}
      >
        <div style={{ color: C.text, fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.full_name || '—'}</span>
          {row.source === 'colleague_invite' || row.invited_by ? (
            <span
              data-testid={`pipeline-paired-chip-${row.request_id}`}
              title={row.invited_by ? `Invited via colleague pair (${row.invited_by})` : 'Colleague invite'}
              style={{
                fontSize: 9,
                letterSpacing: '0.08em',
                padding: '2px 6px',
                border: `1px solid ${C.divider}`,
                color: '#c8a565',
                borderRadius: 2,
                whiteSpace: 'nowrap',
              }}
            >
              PAIRED
            </span>
          ) : null}
          {row.locale === 'ar' ? (
            <span
              data-testid={`pipeline-locale-chip-${row.request_id}`}
              title="Submitted from the Arabic landing page"
              style={{
                fontSize: 9,
                letterSpacing: '0.08em',
                padding: '2px 6px',
                border: `1px solid ${C.divider}`,
                color: 'rgba(255,255,255,0.5)',
                borderRadius: 2,
              }}
            >
              AR
            </span>
          ) : null}
        </div>
        <div style={{ color: C.text, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>
          {row.company_name || '—'}
        </div>
        <div style={{ color: C.muted, fontSize: 12 }}>{roleLabel(row.role)}</div>
        <div style={{ color: C.muted, fontSize: 12 }}>{row.team_size || '—'}</div>
        <div style={{ color: C.muted, fontSize: 11 }}>{relativeDate(row.created_at)}</div>
        <div>
          <span style={{
            display: 'inline-block',
            padding: '3px 10px',
            border: `1px solid ${pill.border}`,
            color: pill.color,
            fontSize: 10,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            borderRadius: 2,
          }}>
            {pill.label}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          {canApprove && (
            <TinyButton testid={`pipeline-approve-${row.request_id}`} onClick={onApprove} disabled={busy} variant="primary">
              {approveLabel}
            </TinyButton>
          )}
          {row.status !== 'rejected' && (
            <TinyButton testid={`pipeline-reject-${row.request_id}`} onClick={onReject} disabled={busy}>
              Decline
            </TinyButton>
          )}
          <TinyButton testid={`pipeline-note-${row.request_id}`} onClick={onNote} disabled={busy}>
            Note…
          </TinyButton>
        </div>
      </div>

      {expanded && (
        <div
          data-testid={`pipeline-row-expanded-${row.request_id}`}
          style={{
            background: C.expanded,
            borderTop: `1px solid ${C.border}`,
            padding: '16px 20px 22px',
            fontSize: 12,
            color: C.muted,
            lineHeight: 1.7,
          }}
        >
          <Field label="Work email">{row.work_email}</Field>
          <Field label="Monthly volume">{row.monthly_volume || '—'}</Field>
          {row.challenge && <Field label="Challenge">{row.challenge}</Field>}
          {row.rejection_reason && <Field label="Rejection reason">{row.rejection_reason}</Field>}
          {row.colleagues && row.colleagues.length > 0 && (
            <Field label="Colleagues invited">
              {row.colleagues.map((c) => c.email || c).join(', ')}
            </Field>
          )}
          {row.notes && row.notes.length > 0 && (
            <Field label="Admin notes">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                {row.notes.map((n, i) => (
                  <div key={i} style={{ color: 'rgba(255,255,255,0.6)' }}>
                    “{n.text}” — <span style={{ color: C.subtle }}>{n.author || 'admin'}, {relativeDate(n.created_at)}</span>
                  </div>
                ))}
              </div>
            </Field>
          )}
        </div>
      )}
    </>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', columnGap: 16, marginBottom: 6 }}>
      <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        {label}
      </div>
      <div style={{ color: 'rgba(255,255,255,0.7)' }}>{children}</div>
    </div>
  );
}

function TinyButton({ children, onClick, disabled, variant, testid }) {
  const primary = variant === 'primary';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      style={{
        background: 'none',
        border: primary ? `1px solid ${C.text}` : 'none',
        color: primary ? '#fff' : C.muted,
        padding: primary ? '4px 10px' : '4px 8px',
        fontSize: 11,
        borderRadius: 2,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        fontFamily: FONT,
        fontWeight: primary ? 500 : 400,
        letterSpacing: '0.02em',
      }}
    >
      {children}
    </button>
  );
}

function EmptyState({ title, subtitle }) {
  return (
    <div
      data-testid="empty-state"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        padding: '64px 24px',
        textAlign: 'center',
      }}
    >
      <div style={{ color: C.text, fontSize: 14, marginBottom: 6 }}>{title}</div>
      <div style={{ color: C.muted, fontSize: 13 }}>{subtitle}</div>
    </div>
  );
}

// ── Direct-invite modal (opens from Pipeline tab) ──────────────────
function InviteModal({ password, backendUrl, onClose, onSuccess }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [sending, setSending] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [success, setSuccess] = useState(false);

  const canSubmit = !sending && name.trim() && email.trim() && company.trim();

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    setInviteError('');
    try {
      const r = await fetch(`${backendUrl}/api/admin/direct-invite?password=${encodeURIComponent(password)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          full_name: name.trim(),
          work_email: email.trim().toLowerCase(),
          company_name: company.trim(),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.detail || 'Invite failed');
      setSuccess(true);
      setTimeout(() => {
        onSuccess?.(email.trim().toLowerCase());
        onClose();
      }, 1500);
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="invite-modal"
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 4,
          padding: 32,
          width: 400,
          fontFamily: FONT,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 6 }}>
          Invite someone directly
        </div>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', marginBottom: 24 }}>
          They will receive the welcome email immediately. No waitlist required.
        </div>

        <InviteField label="Full name" testid="invite-name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ahmed Al-Rashid"
            data-testid="invite-name-input"
            autoFocus
            style={inviteInputStyle}
          />
        </InviteField>

        <InviteField label="Work email" testid="invite-email">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="ahmed@company.com"
            data-testid="invite-email-input"
            style={inviteInputStyle}
          />
        </InviteField>

        <InviteField label="Company" testid="invite-company">
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Aramco"
            data-testid="invite-company-input"
            style={inviteInputStyle}
          />
        </InviteField>

        {inviteError && (
          <div data-testid="invite-error" style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginBottom: 14 }}>
            {inviteError}
          </div>
        )}
        {success && (
          <div data-testid="invite-success" style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 14 }}>
            ✓ Invitation sent to {email}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="invite-submit-btn"
            style={{
              flex: 1, background: '#fff', color: '#000', border: 'none',
              padding: 10, borderRadius: 3, fontSize: 13,
              fontWeight: 600,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              opacity: canSubmit ? 1 : 0.4,
              fontFamily: FONT,
            }}
          >
            {sending ? 'Sending…' : 'Send invitation'}
          </button>
          <button
            type="button"
            onClick={onClose}
            data-testid="invite-cancel-btn"
            style={{
              background: 'none', color: C.muted,
              border: `1px solid ${C.divider}`,
              padding: '10px 16px', borderRadius: 3,
              fontSize: 13, cursor: 'pointer',
              fontFamily: FONT,
            }}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

const inviteInputStyle = {
  width: '100%', background: C.bg, border: `1px solid ${C.divider}`,
  borderRadius: 3, padding: '10px 14px', color: C.text,
  fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: FONT,
};

function InviteField({ label, children, testid }) {
  return (
    <div data-testid={testid} style={{ marginBottom: 14 }}>
      <label style={{
        fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.3)', display: 'block', marginBottom: 6,
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}


// ── Metrics tab ────────────────────────────────────────────────────
function MetricsTab({ password, backendUrl, stats }) {
  const [metrics, setMetrics] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${backendUrl}/api/admin/investor-metrics?password=${encodeURIComponent(password)}`)
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data?.detail || 'Failed');
        setMetrics(data);
      })
      .catch((e) => setError(e.message));
  }, [password, backendUrl]);

  const m = metrics || {};
  const waitlist = m.waitlist || {};
  const trials = m.trials || {};
  const conv = trials.conversion_rate_pct;

  const headlineKpis = [
    { label: 'Total signups',    value: waitlist.total },
    { label: 'Companies',        value: waitlist.unique_companies },
    { label: 'This week',        value: waitlist.this_week },
    { label: 'Conversion rate',  value: conv === null || conv === undefined ? null : `${conv}%` },
  ];
  const secondaryKpis = [
    { label: 'Total questions asked', value: stats?.metrics?.total_questions },
    { label: 'Active today',          value: stats?.metrics?.active_users_today },
    { label: 'Avg questions / user',  value: stats?.metrics?.avg_questions_per_user },
    { label: 'Satisfaction rate',     value: stats?.metrics?.satisfaction_rate === null || stats?.metrics?.satisfaction_rate === undefined ? null : `${stats.metrics.satisfaction_rate}%` },
  ];

  return (
    <section data-testid="admin-metrics-tab">
      {error && <div style={{ color: C.muted, fontSize: 12, marginBottom: 16 }}>{error}</div>}

      {/* Headline KPIs */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 1, background: C.border,
        border: `1px solid ${C.border}`, borderRadius: 3,
        overflow: 'hidden', marginBottom: 24,
      }}>
        {headlineKpis.map((k) => (
          <div
            key={k.label}
            data-testid={`metrics-kpi-${k.label.toLowerCase().replace(/\s+/g, '-')}`}
            style={{ background: C.bg, padding: '32px 28px' }}
          >
            <div style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginBottom: 12 }}>
              {k.label}
            </div>
            <div style={{
              fontSize: 48, fontWeight: 700, lineHeight: 1,
              fontFamily: 'Georgia, serif',
              color: k.value === null || k.value === undefined || k.value === 0 || k.value === '0%' ? 'rgba(255,255,255,0.25)' : C.text,
            }}>
              {k.value === null || k.value === undefined ? '—' : k.value === 0 ? '0' : fmt(k.value) || k.value}
            </div>
          </div>
        ))}
      </div>

      {/* Middle: trend + roles */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
        <Panel title="Signup trend · last 14 days" testid="metrics-trend">
          <SignupTrend data={m.signup_trend_14d} />
        </Panel>
        <Panel title="Role breakdown" testid="metrics-roles">
          <RoleBreakdown data={m.role_breakdown} />
        </Panel>
      </div>

      {/* Secondary compact KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {secondaryKpis.map((k) => {
          const empty = k.value === null || k.value === undefined;
          return (
            <div
              key={k.label}
              data-testid={`metrics-sub-${k.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 3, padding: 20 }}
            >
              <div style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.25)', marginBottom: 8 }}>
                {k.label}
              </div>
              <div style={{
                fontSize: empty ? 13 : 28, fontWeight: empty ? 400 : 600, lineHeight: 1,
                color: empty || k.value === 0 || k.value === '0%' ? 'rgba(255,255,255,0.25)' : C.text,
              }}>
                {empty ? 'No data yet' : k.value === 0 ? '0' : fmt(k.value) || k.value}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Panel({ title, testid, children }) {
  return (
    <div
      data-testid={testid}
      style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 3, padding: 20, minHeight: 200 }}
    >
      <div style={{ fontSize: 11, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)', marginBottom: 16 }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function SignupTrend({ data }) {
  const pts = (data || []).map((d) => ({ date: d.date, count: d.count ?? 0 }));
  if (!pts.length || pts.every((p) => p.count === 0)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 140, color: C.muted, fontSize: 13 }}>
        No signups in the last 14 days.
      </div>
    );
  }
  const w = 520, h = 140, padX = 8, padY = 16;
  const maxV = Math.max(...pts.map((p) => p.count), 1);

  // If only one data point, render it centered as a dot + tagline (no line).
  if (pts.length === 1) {
    return (
      <div>
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none">
          <line x1={padX} x2={w - padX} y1={h - padY} y2={h - padY} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
          <circle cx={w / 2} cy={h / 2} r="3" fill="#FFFFFF" />
        </svg>
        <div style={{ display: 'flex', justifyContent: 'center', fontSize: 11, color: C.muted, marginTop: 4 }}>
          {pts[0].count} signup{pts[0].count === 1 ? '' : 's'} on {pts[0].date}
        </div>
      </div>
    );
  }

  const stepX = (w - padX * 2) / (pts.length - 1);
  const ptsXY = pts.map((p, i) => {
    const x = padX + i * stepX;
    const y = h - padY - ((p.count / maxV) * (h - padY * 2));
    return { ...p, x, y };
  });
  const linePath = ptsXY.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  const firstDate = pts[0]?.date;
  const lastDate = pts[pts.length - 1]?.date;

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} preserveAspectRatio="none">
        <line x1={padX} x2={w - padX} y1={h - padY} y2={h - padY} stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
        <path d={linePath} stroke="#FFFFFF" strokeWidth="1.5" fill="none" />
        {ptsXY.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r="2" fill="#FFFFFF" />
        ))}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: C.subtle, marginTop: 4 }}>
        <span>{firstDate}</span>
        <span>{lastDate}</span>
      </div>
    </div>
  );
}

function RoleBreakdown({ data }) {
  const items = (data || []).filter((r) => (r.count ?? 0) > 0);
  const total = items.reduce((s, r) => s + (r.count ?? 0), 0);
  if (!total) return <div style={{ color: C.muted, fontSize: 13 }}>No role data yet.</div>;
  if (items.length === 1) {
    return (
      <div style={{ fontSize: 18, color: C.text }}>
        100% <span style={{ color: C.muted, fontSize: 13, marginLeft: 6 }}>{roleLabel(items[0].role)}</span>
      </div>
    );
  }
  const sorted = [...items].sort((a, b) => (b.count ?? 0) - (a.count ?? 0));
  return (
    <div>
      <div style={{ display: 'flex', height: 8, borderRadius: 2, overflow: 'hidden', marginBottom: 16, background: C.border }}>
        {sorted.map((r, i) => {
          const pct = ((r.count ?? 0) / total) * 100;
          const opacity = 1 - i * 0.13;
          return (
            <div
              key={r.role}
              style={{ width: `${pct}%`, background: `rgba(255,255,255,${Math.max(opacity, 0.2)})` }}
              title={`${roleLabel(r.role)} — ${pct.toFixed(0)}%`}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {sorted.map((r, i) => {
          const pct = ((r.count ?? 0) / total) * 100;
          const opacity = 1 - i * 0.13;
          return (
            <div key={r.role} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-block', width: 8, height: 8, background: `rgba(255,255,255,${Math.max(opacity, 0.2)})` }} />
                <span style={{ color: C.muted }}>{roleLabel(r.role)}</span>
              </span>
              <span style={{ color: C.text }}>{pct.toFixed(0)}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Operations tab ─────────────────────────────────────────────────
function OperationsTab({ stats }) {
  const questions = stats?.recent_questions || [];
  const users = stats?.users || [];
  const weak = stats?.weak_responses || [];

  return (
    <section data-testid="admin-operations-tab" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <Collapsible label="Recent Questions" count={questions.length} testid="ops-questions">
        <QuestionsTable rows={questions} />
      </Collapsible>
      <Collapsible label="User Activity" count={users.length} testid="ops-users">
        <UsersTable rows={users} />
      </Collapsible>
      <Collapsible label="Flagged Responses" count={weak.length} testid="ops-weak">
        <WeakTable rows={weak} />
      </Collapsible>
    </section>
  );
}

function Collapsible({ label, count, testid, children }) {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 3, overflow: 'hidden' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid={`${testid}-toggle`}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 14,
          background: 'none', border: 'none', color: C.text,
          padding: '16px 20px', cursor: 'pointer', fontFamily: FONT,
          fontSize: 13, textAlign: 'left',
        }}
      >
        <span style={{ color: C.muted, fontSize: 11, width: 12 }}>{open ? '▼' : '▶'}</span>
        <span style={{ fontWeight: 500 }}>{label}</span>
        <span style={{ color: C.muted, fontSize: 12, marginLeft: 'auto' }}>
          ({fmt(count) || 0})
        </span>
      </button>
      {open && (
        <div style={{ borderTop: `1px solid ${C.border}` }}>
          {count === 0 ? (
            <div style={{ padding: '32px 20px', color: C.muted, fontSize: 13, textAlign: 'center' }}>
              No data yet.
            </div>
          ) : children}
        </div>
      )}
    </div>
  );
}

function SimpleTable({ columns, rows, getKey, getCell, testid }) {
  return (
    <div data-testid={testid} style={{ overflowX: 'auto' }}>
      <div style={{
        display: 'grid', gridTemplateColumns: columns.map((c) => c.width || '1fr').join(' '),
        padding: '10px 20px', borderBottom: `1px solid ${C.border}`, gap: 12,
        fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)',
      }}>
        {columns.map((c) => <div key={c.key}>{c.label}</div>)}
      </div>
      {rows.slice(0, 50).map((row, i) => (
        <div
          key={getKey(row, i)}
          style={{
            display: 'grid', gridTemplateColumns: columns.map((c) => c.width || '1fr').join(' '),
            padding: '10px 20px', borderTop: `1px solid ${C.border}`, gap: 12,
            fontSize: 12, color: C.text,
          }}
        >
          {columns.map((c) => <div key={c.key} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getCell(row, c)}</div>)}
        </div>
      ))}
    </div>
  );
}

function QuestionsTable({ rows }) {
  return (
    <SimpleTable
      testid="ops-questions-table"
      columns={[
        { key: 'time', label: 'When', width: '0.7fr' },
        { key: 'user', label: 'User', width: '1.2fr' },
        { key: 'question', label: 'Question', width: '3fr' },
        { key: 'law', label: 'Law system', width: '0.9fr' },
        { key: 'fb', label: 'Feedback', width: '0.6fr' },
      ]}
      rows={rows}
      getKey={(r, i) => r.id || r._id || i}
      getCell={(r, c) => {
        if (c.key === 'time') return <span style={{ color: C.muted }}>{relativeDate(r.timestamp || r.created_at)}</span>;
        if (c.key === 'user') return <span style={{ color: C.muted }}>{r.user_email || r.user_id || '—'}</span>;
        if (c.key === 'question') return <span title={r.question}>{r.question}</span>;
        if (c.key === 'law') return <span style={{ color: C.muted }}>{r.law_system || '—'}</span>;
        if (c.key === 'fb') return <span style={{ color: C.muted }}>{r.feedback || '—'}</span>;
        return '';
      }}
    />
  );
}

function UsersTable({ rows }) {
  return (
    <SimpleTable
      testid="ops-users-table"
      columns={[
        { key: 'email', label: 'Email', width: '2fr' },
        { key: 'q', label: 'Questions', width: '0.7fr' },
        { key: 'sessions', label: 'Sessions', width: '0.7fr' },
        { key: 'last', label: 'Last active', width: '1fr' },
        { key: 'sat', label: 'Satisfaction', width: '0.8fr' },
      ]}
      rows={rows}
      getKey={(r, i) => r.email || r._id || i}
      getCell={(r, c) => {
        if (c.key === 'email') return r.email || '—';
        if (c.key === 'q') return <span style={{ color: C.muted }}>{fmt(r.question_count ?? r.questions ?? 0)}</span>;
        if (c.key === 'sessions') return <span style={{ color: C.muted }}>{fmt(r.session_count ?? 0)}</span>;
        if (c.key === 'last') return <span style={{ color: C.muted }}>{relativeDate(r.last_activity || r.last_active)}</span>;
        if (c.key === 'sat') {
          const v = r.satisfaction_rate;
          return <span style={{ color: C.muted }}>{v === undefined || v === null ? '—' : `${v}%`}</span>;
        }
        return '';
      }}
    />
  );
}

function WeakTable({ rows }) {
  return (
    <SimpleTable
      testid="ops-weak-table"
      columns={[
        { key: 'time', label: 'When', width: '0.7fr' },
        { key: 'q', label: 'Question', width: '2.5fr' },
        { key: 'issues', label: 'Issues', width: '2fr' },
      ]}
      rows={rows}
      getKey={(r, i) => r.id || i}
      getCell={(r, c) => {
        if (c.key === 'time') return <span style={{ color: C.muted }}>{relativeDate(r.timestamp || r.created_at)}</span>;
        if (c.key === 'q') return <span title={r.question}>{r.question}</span>;
        if (c.key === 'issues') return <span style={{ color: C.muted }}>{(r.issues || []).join(', ') || '—'}</span>;
        return '';
      }}
    />
  );
}

// ── Debug strip (hidden behind ?debug=1) ───────────────────────────
function DebugStrip({ version }) {
  return (
    <div
      data-testid="admin-debug-strip"
      style={{
        position: 'fixed', bottom: 12, left: '50%', transform: 'translateX(-50%)',
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: 999,
        padding: '6px 16px', display: 'flex', gap: 14,
        fontFamily: FONT, fontSize: 10, color: C.muted, letterSpacing: '0.04em',
      }}
    >
      <span>{version.deploy_sha || '—'}</span>
      <span>·</span>
      <span>{version.model_review || '—'}</span>
      <span>·</span>
      <span style={{ color: version.tavily_set ? 'rgba(255,255,255,0.6)' : '#911' }}>
        Tavily {version.tavily_set ? '✓' : '✗'}
      </span>
      <span>·</span>
      <span style={{ color: version.admin_password_set ? 'rgba(255,255,255,0.6)' : '#911' }}>
        Admin {version.admin_password_set ? '✓' : '✗'}
      </span>
    </div>
  );
}
